import { DeleteOutlined, HistoryOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import type { ViewFieldsFragment } from '@salescore/client-api'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
} from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { useBooleanState } from '@salescore/frontend-common'
import type { ModalFuncProps } from 'antd'
import { Button, message, Popover, Space, Table, Tag } from 'antd'
import dayjs from 'dayjs'
import { Array, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useMemo } from 'react'
import { useRecoilValue } from 'recoil'

import { getChangesChunks } from '../../../../domain/recordsHistory/recordsHistorySelectors'
import { useRefetchMutation } from '../../../../recoil/records/mutations/useRefetchMutation'
import { changesAtom, useBackHistory } from '../../../../recoil/recordsHistory'
import { buildNamesWithColors } from '../../../../recoil/view/selectors/querySelector'
import { configAtom } from '../../../../recoil/viewConfig'
import { flatNodes } from '../../../../state/nodeUtility'

export function buildChangesModalProperties(
  // eslint-disable-next-line sonarjs/use-type-alias
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
  query: ViewQueryList | undefined,
): ModalFuncProps {
  return {
    icon: null,
    width: '80%',
    cancelText: t(`閉じる`),
    okButtonProps: { style: { display: 'none' } },
    closable: true,
    maskClosable: true,
    destroyOnClose: true,
    content: (
      <>
        <ChangesTableContent compiledConfig={compiledConfig} view={view} query={query} />
        <HistoryTableContent compiledConfig={compiledConfig} query={query} />
      </>
    ),
  }
}

const ChangesTableContent = ({
  compiledConfig,
  view,
  query,
}: {
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList | undefined
}): ReactElement => {
  const changes = useRecoilValue(changesAtom)
  const config = useRecoilValue(configAtom)
  const changesChunks = useMemo(() => getChangesChunks(changes, config), [changes, config])

  const recordsMutation = useRefetchMutation(view, query)
  // eslint-disable-next-line sonarjs/deprecation
  const loading = useBooleanState()
  const streamNameToLabel = useStreamLabel(query)
  const streamNameToColor = useStreamColor(query)
  const propertyNameToLabel = usePropertyLabel(query)

  return (
    <PageHeader
      title={t(`変更内容`)}
      extra={[
        <Button
          key="reset"
          disabled={changes.length === 0}
          danger
          icon={<DeleteOutlined />}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
          onClick={async () => {
            loading.setTrue()
            try {
              await recordsMutation.refetch({ kind: 'reset' })
            } catch (error) {
              if (error instanceof Error) {
                void message.error(error.message)
              }
            } finally {
              loading.setFalse()
            }
          }}
        >
          {t(`変更内容をリセット`)}
        </Button>,
      ]}
    >
      <Table
        size="small"
        dataSource={changesChunks.flatMap((x) => x.changes)}
        columns={[
          {
            title: t(`オブジェクト名`),
            key: 'name',
            width: 180,
            render(_, change) {
              const color = streamNameToColor[change.modelName] ?? 'gray'
              return <Tag color={color}>{streamNameToLabel[change.modelName] ?? change.modelName}</Tag>
            },
          },
          {
            title: t(`変更したレコード(ID)`),
            key: 'id',
            width: 200,
            render(_, change) {
              if (change.type === 'create') {
                return <Tag>{t(`新規レコード`)}</Tag>
              }
              const { id, recordNameLabel } = change
              if (recordNameLabel !== undefined) {
                return (
                  <Popover content={id}>
                    <Tag>{recordNameLabel}</Tag>
                  </Popover>
                )
              }
              return null
            },
          },
          {
            title: t(`変更前`),
            key: 'before',
            render(_, change) {
              if (change.type === 'create') {
                return null
              }

              return (
                <div>
                  {Record.collect(change.before, (key, value) => (
                    <div key={key}>
                      <>
                        {propertyNameToLabel[key] ?? key} : {value ?? t(`入力なし`)}
                      </>
                    </div>
                  ))}
                </div>
              )
            },
          },
          {
            title: t(`変更後`),
            key: 'after',
            render(_, change) {
              if (change.type === 'delete') {
                return <span className="text-red-400">{t(`レコードを削除`)}</span>
              }
              return (
                <div>
                  {Record.collect(change.data, (key: string, value: unknown) => (
                    <div key={key}>
                      <>
                        {propertyNameToLabel[key] ?? key} : {value}
                      </>
                    </div>
                  ))}
                </div>
              )
            },
          },
        ]}
      />
    </PageHeader>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function HistoryTableContent({
  compiledConfig,
  query,
}: {
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  query: ViewQueryList | undefined
}) {
  const changes = useRecoilValue(changesAtom)
  const backHistory = useBackHistory()
  const streamNameToLabel = useStreamLabel(query)
  const streamNameToColor = useStreamColor(query)
  const propertyNameToLabel = usePropertyLabel(query)

  return (
    <PageHeader
      title={
        <Space>
          <HistoryOutlined /> {t(`変更履歴`)}
        </Space>
      }
    >
      <Table
        size="small"
        dataSource={changes.toReversed().map((x, index) => ({ ...x, key: x.datetime, index }))}
        columns={[
          {
            title: t(`変更時刻`),
            key: 'datetime',
            width: 200,
            render(_, record) {
              return <div>{dayjs(record.datetime).format(`HH:mm:ss.SSS`)}</div>
            },
          },
          {
            title: t(`オブジェクト名`),
            key: 'name',
            width: 250,
            render(_, record) {
              return (
                <div>
                  {record.changes.map((change) => {
                    const color = streamNameToColor[change.streamName] ?? 'gray'
                    return (
                      <Tag key={change.streamName} color={color}>
                        {streamNameToLabel[change.streamName] ?? change.streamName}
                      </Tag>
                    )
                  })}
                </div>
              )
            },
          },
          {
            title: t(`変更したレコード(ID)`),
            key: 'id',
            width: 220,
            render(_, record) {
              return (
                <div>
                  {record.changes.map((change) => {
                    const { id, recordNameLabel } = change
                    if (recordNameLabel !== undefined) {
                      return (
                        <Popover key={id} content={id}>
                          <Tag>{recordNameLabel}</Tag>
                        </Popover>
                      )
                    }
                    return <Tag key={id}>{id ?? t(`新規レコード`)}</Tag>
                  })}
                </div>
              )
            },
          },
          {
            title: t(`変更前`),
            key: 'before',
            render(_, record) {
              return (
                <div>
                  {record.changes.map((change) =>
                    Record.collect(change.before ?? {}, (key, value) => (
                      <div key={key}>
                        <>
                          {propertyNameToLabel[key] ?? key} : {value ?? t(`入力なし`)}
                        </>
                      </div>
                    )),
                  )}
                </div>
              )
            },
          },
          {
            title: t(`変更後`),
            key: 'after',
            render(_, record) {
              return (
                <div>
                  {record.changes.map((change) => (
                    <div key={change.streamName}>
                      {Record.collect(change.after ?? {}, (key, value) => (
                        <div key={key}>
                          <>
                            {propertyNameToLabel[key] ?? key} : {value ?? t(`入力なし`)}
                          </>
                        </div>
                      ))}
                      {change.deleted === true ? <span className="text-red-400">{t(`レコードを削除`)}</span> : ''}
                    </div>
                  ))}
                </div>
              )
            },
          },
          {
            title: '',
            key: 'back',
            render(_, record) {
              if (record.index === 0) {
                return null
              }

              return (
                <Button
                  onClick={() => {
                    const targetIndex = record.index
                    backHistory(targetIndex)
                  }}
                >
                  {t(`この状態に戻す`)}
                </Button>
              )
            },
          },
        ]}
      />
    </PageHeader>
  )
}

function useStreamLabel(query: ViewQueryList | undefined) {
  if (query === undefined) {
    return {}
  }
  const nodes = flatNodes(query.tree)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion,sonarjs/todo-tag
  return Record.fromEntries(nodes.map((node) => [node.write?.streamName ?? '', node.meta.label.split('(')[0]!])) // TODO: stream.meta.labelを取得したい
}

function useStreamColor(query: ViewQueryList | undefined) {
  const nodeNamesWithColors = useMemo(() => (query === undefined ? {} : buildNamesWithColors(query)), [query])
  if (query === undefined) {
    return {}
  }
  const nodes = flatNodes(query.tree)
  return pipe(
    nodes,
    Array.map((node) => {
      const color = nodeNamesWithColors[node.name]
      if (color === undefined) {
        return null
      }
      return [node.write?.streamName ?? '', color] as const
    }),
    Array.filter(isNotNullish),
    Record.fromEntries,
  )
}

function usePropertyLabel(query: ViewQueryList | undefined) {
  if (query === undefined) {
    return {}
  }

  return {
    ...Record.fromIterableWith(
      query.fields,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (field) => [field.write?.propertySourceName ?? '', field.meta.label.split('(')[0]!] as const,
    ),
    ...Record.fromIterableWith(
      query.fields,
      (field) => [field.meta.dependedPropertyNamesWithStreamName[0]?.propertyName ?? '', field.meta.label] as const,
    ),
  }
}
